import { __assign, __extends, __read, __values } from "tslib";
import { logInDev } from '../../../foundation';
import { Connectable, ConnectSymbol, ThrottleScheduler, } from '../Connectable';
import { PortalStore } from './PortalStore';
import { UnmountSymbol, } from './types';
import { createPromise, UniqIdUtil } from './utils';
var PortalManager = /** @class */ (function (_super) {
    __extends(PortalManager, _super);
    function PortalManager(scheduler) {
        if (scheduler === void 0) { scheduler = new ThrottleScheduler(); }
        var _this = _super.call(this, scheduler) || this;
        /**
         * you can custom your manager by control this
         */
        _this.portalStore = new PortalStore();
        _this._feedbackMap = new Map();
        _this._uniqIdUtils = new UniqIdUtil();
        _this.portalStore[ConnectSymbol](function (portals) { return _this.emit(portals); });
        return _this;
    }
    /**
     * open a component with portal (Dialog, snackbar, modal...)
     * @param Component the component you want to open
     * @param options some options (id, props...)
     */
    PortalManager.prototype.open = function (Component, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var _id = options.id, props = options.props, data = options.data;
        var id = _id !== null && _id !== void 0 ? _id : this._uniqIdUtils.get();
        var currPortal = this.portalStore.get(id);
        // portalWithSameID opened
        if (currPortal === null || currPortal === void 0 ? void 0 : currPortal.open) {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcPortalHost',
                    message: "open the portal with id('" + id + "') failed, this portal is already open",
                });
            }
            return currPortal.portalController;
        }
        var portalDescriptor = this.createPortalDescriptor({
            Component: Component,
            id: id,
            props: props,
            data: data,
        });
        // portalWithSameID closed and not unmount
        if (currPortal) {
            // when closing (Animating), trigger open again.
            currPortal.portalController.onClosed.then(function () {
                _this._openPortal(portalDescriptor);
            });
        }
        // portalWithSameID unmounted
        else {
            this._openPortal(portalDescriptor);
        }
        return portalDescriptor.portalController;
    };
    /**
     * close all
     */
    PortalManager.prototype.closeAll = function () {
        var _this = this;
        this.portalStore.batch(function (store) {
            var e_1, _a;
            try {
                for (var _b = __values(_this.portalStore.portals), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var portal = _c.value;
                    store.addOrUpdate(__assign(__assign({}, portal), { open: false }));
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        });
    };
    /**
     * get portal controller which on top
     */
    PortalManager.prototype.getTop = function () {
        var _a;
        return (_a = this.portalStore.lastPortal) === null || _a === void 0 ? void 0 : _a.portalController;
    };
    /**
     * get portal controller by id
     */
    PortalManager.prototype.getByID = function (id) {
        var _a;
        return (_a = this.portalStore.get(id)) === null || _a === void 0 ? void 0 : _a.portalController;
    };
    /**
     * check if it's opened by id. equal 'portalController.isOpened'
     */
    PortalManager.prototype.isOpened = function (id) {
        var portal = this.portalStore.get(id);
        return Boolean(portal === null || portal === void 0 ? void 0 : portal.open);
    };
    /**
     * check if it's on top by id. equal 'portalController.isTop'
     */
    PortalManager.prototype.isTop = function (id) {
        var _a;
        return ((_a = this.portalStore.lastPortal) === null || _a === void 0 ? void 0 : _a.id) === id;
    };
    /**
     * for RC component (Modal, component based on modal, snackbar).
     * invoke this function would set 'open' prop to be false
     * then unmount component after animation finish
     *
     * for non-RC component, you need unmount by self or call unmount directly
     *
     * equal 'portalController.close()'
     */
    PortalManager.prototype.closeByID = function (id, feedback) {
        var portal = this.portalStore.get(id);
        if (!(portal === null || portal === void 0 ? void 0 : portal.open)) {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcPortalHost',
                    message: 'close portal failed, this portal is already closed',
                });
            }
            return;
        }
        this._feedbackMap.set(portal.id, feedback);
        this.portalStore.addOrUpdate(__assign(__assign({}, portal), { open: false }));
    };
    /**
     * invoke this to close portal is not recommend, will loss close animation
     *
     * this function just help you unmount component after close animation finish
     *
     * RC component (Modal, component based on modal, snackbar) would invoke this after close animation finish
     */
    PortalManager.prototype[UnmountSymbol] = function (id) {
        var portal = this.portalStore.get(id);
        if (!portal) {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcPortalHost',
                    message: 'unmount portal failed, this portal is already unmount',
                });
            }
            return;
        }
        this.portalStore.delete(portal.id);
    };
    /**
     * update props by id, equal 'portalController.updateProps(newProps)'
     */
    PortalManager.prototype.updatePropsByID = function (id, propsOrHandler) {
        var portal = this.portalStore.get(id);
        if (!(portal === null || portal === void 0 ? void 0 : portal.open)) {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcPortalHost',
                    message: 'update props failed, this portal is already closed',
                });
            }
            return;
        }
        var props = (function () {
            if (typeof propsOrHandler === 'function') {
                return propsOrHandler(portal.props);
            }
            return propsOrHandler;
        })();
        this.portalStore.addOrUpdate(__assign(__assign({}, portal), { props: props }));
    };
    PortalManager.prototype.onConnected = function () {
        _super.prototype.onConnected.call(this);
        // prevent invoke 'open()' fail before PortalHost connect this
        this.portalStore.forceEmit();
    };
    PortalManager.prototype.onDisconnected = function () {
        _super.prototype.onDisconnected.call(this);
        // clean all portal after PortalHost disconnect this
        this.portalStore.clear();
        this._feedbackMap.clear();
    };
    PortalManager.prototype._openPortal = function (portal) {
        this.portalStore.addOrUpdate(portal);
    };
    PortalManager.prototype.createPortalDescriptor = function (_a) {
        var _this = this;
        var props = _a.props, data = _a.data, Component = _a.Component, id = _a.id;
        var _b = __read(createPromise(), 2), onOpened = _b[0], resolveOnOpened = _b[1];
        var _c = __read(createPromise(), 2), onClosed = _c[0], resolveOnClosed = _c[1];
        var isTop = function () { return _this.isTop(id); };
        var isOpened = function () { return _this.isOpened(id); };
        var portalController = {
            id: id,
            onOpened: onOpened,
            onClosed: onClosed,
            close: function (feedback) {
                _this.closeByID(id, feedback);
            },
            updateProps: function (propsOrHandler) {
                _this.updatePropsByID(id, propsOrHandler);
            },
            data: data,
            get isOpened() {
                return isOpened();
            },
            get isTop() {
                return isTop();
            },
        };
        var portalDescriptor = {
            id: id,
            Component: Component,
            onMounted: function () {
                resolveOnOpened();
            },
            onUnmounted: function () {
                var feedback = _this._feedbackMap.get(id);
                _this._feedbackMap.delete(id);
                resolveOnClosed(feedback);
            },
            props: props,
            onClose: function (feedback) {
                _this.closeByID(id, feedback);
            },
            open: true,
            portalController: portalController,
        };
        return portalDescriptor;
    };
    return PortalManager;
}(Connectable));
export { PortalManager };
